/**
 * Rich-text clipboard helper.
 *
 * Pasting a signature into Gmail only keeps its formatting if the clipboard
 * carries a `text/html` flavour. Copying the markup as plain text would paste
 * visible tags, so this is not a nicety — it is what makes the feature work.
 *
 * Two paths, because no single one covers every browser we may meet:
 *
 *   1. The async Clipboard API (`navigator.clipboard.write` + `ClipboardItem`).
 *      Chrome, Edge, Safari 13.1+, Firefox 127+.
 *   2. A hidden contenteditable + `document.execCommand("copy")`. Deprecated,
 *      but it is the only thing older Firefox understands, and it still works
 *      everywhere else as a safety net.
 *
 * Both must run inside the user gesture that triggered them — never await
 * anything before calling this.
 */

/** Strip tags for the plain-text flavour when no text twin was supplied. */
function stripTags(html) {
  const el = document.createElement("div");
  el.innerHTML = String(html || "");
  return (el.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Copy `html` as rich text, with `text` as the plain-text flavour.
 * Returns the path that succeeded ("clipboard-api" | "exec-command"), or
 * `false` when both failed — the caller decides what to tell the user.
 */
export async function copyRichText(html, text) {
  const markup = String(html || "");
  if (!markup) return false;
  const plain = text != null && text !== "" ? String(text) : stripTags(markup);

  if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([markup], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
      return "clipboard-api";
    } catch {
      // Permission denied, a non-secure context, or a browser that advertises
      // ClipboardItem but refuses text/html. Fall through to the legacy path.
    }
  }

  return copyViaSelection(markup) ? "exec-command" : false;
}

/**
 * Legacy path: render the markup into an off-screen contenteditable, select
 * it, and let the browser put the formatted selection on the clipboard.
 *
 * The node must be visible to the layout engine for the selection to take, so
 * it is positioned off-screen rather than `display:none`. It is always removed,
 * including when execCommand throws.
 */
function copyViaSelection(markup) {
  const holder = document.createElement("div");
  holder.setAttribute("contenteditable", "true");
  holder.innerHTML = markup;
  holder.style.cssText =
    "position:fixed;left:-9999px;top:0;opacity:0;white-space:normal;";
  document.body.appendChild(holder);

  try {
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(holder);
    selection.removeAllRanges();
    selection.addRange(range);
    const ok = document.execCommand("copy");
    selection.removeAllRanges();
    return ok;
  } catch {
    return false;
  } finally {
    holder.remove();
  }
}

/** Plain-text copy, for the raw-HTML box where tags ARE the content. */
export async function copyPlainText(value) {
  const text = String(value || "");
  if (!text) return false;
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return "clipboard-api";
    } catch { /* fall through */ }
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.cssText = "position:fixed;left:-9999px;top:0;";
  document.body.appendChild(ta);
  try {
    ta.select();
    return document.execCommand("copy") ? "exec-command" : false;
  } catch {
    return false;
  } finally {
    ta.remove();
  }
}
